import React, { useEffect, useCallback, useContext } from 'react';
import { notification, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SessionContext from '@providers/Session/SessionContext';
import {
  AlarmData,
  AlarmNotificationContent,
} from '@components/AlarmNotificationContent/AlarmNotificationContent';
import {
  useNotificationToast,
  ToastNotificationItem,
} from '@hooks/useNotificationToast';
import { NotificationItem } from '@hooks/useNotifications';

interface IAlarmNotificationProps {
  openNotification?: boolean;
}

export function AlarmToastNotification({
  openNotification = false,
}: IAlarmNotificationProps) {
  const { theme } = useContext(SessionContext);
  const [api, contextHolder] = notification.useNotification();
  const { t } = useTranslation(['alerts', 'alarms-notification']);
  const navigate = useNavigate();

  const {
    newToastNotifications,
    handleRemoveToastNotification,
    handleToastNotificationClick,
    markToastAsDisplayed,
  } = useNotificationToast();

  const openSingleNotificationHandler = useCallback(
    (alarmData: AlarmData, originalNotification: ToastNotificationItem) => {
      const notificationKey = `alarm-${alarmData.idAlarme}-${Date.now()}`;
      const closeMessage = () => notification.close(notificationKey);

      api.open({
        key: notificationKey,
        message: (
          <div style={{ position: 'relative', paddingRight: '30px' }}>
            <Button
              icon={<CloseOutlined />}
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                fontSize: '14px',
                color: '#999',
                padding: 0,
              }}
              type="link"
              onClick={closeMessage}
            />
            <AlarmNotificationContent
              navigateToAlarmPage={() => {
                closeMessage();
                handleToastNotificationClick(originalNotification);
              }}
              alarmData={alarmData}
              isToast
            />
          </div>
        ),
        description: '',
        style: {
          padding: 0,
          backgroundColor: theme.colors.background,
        },
        className: 'notification-toast',
        duration: 10,
        placement: 'top',
        closeIcon: null,
        onClose() {
          handleRemoveToastNotification(alarmData.idAlarme, alarmData.timeId);
        },
      });

      markToastAsDisplayed(alarmData.idAlarme);
    },
    [
      api,
      handleToastNotificationClick,
      theme.colors.background,
      handleRemoveToastNotification,
      markToastAsDisplayed,
    ],
  );

  const openMultipleNotificationsHandler = useCallback(
    (notifications: NotificationItem[]) => {
      const notificationKey = `multiple-alarms-${Date.now()}`;
      const closeMessage = () => notification.close(notificationKey);
      const count = notifications.length;

      api.open({
        key: notificationKey,
        message: (
          <div style={{ position: 'relative' }}>
            <Button
              icon={<CloseOutlined />}
              style={{
                position: 'absolute',
                top: 0,
                right: -20,
                fontSize: '14px',
                color: '#999',
                padding: 0,
              }}
              type="link"
              onClick={closeMessage}
            />
            <div style={{ padding: '16px', textAlign: 'center' }}>
              <strong>
                {t('alerts:titles.alarm-trigger-event', {
                  count,
                })}
              </strong>
            </div>
            <div style={{ padding: '0 16px 16px 16px', textAlign: 'center' }}>
              <Button
                type="link"
                onClick={() => {
                  closeMessage();
                  navigate('/indicators/alarms/page-alarms-notification');
                }}
              >
                {t('alarms-notification:viewAlarms')}
              </Button>
              <Button
                type="link"
                onClick={() => {
                  closeMessage();
                  navigate('/indicators/alarms/page-alarms');
                }}
              >
                {t('alarms-notification:configureAlarms')}
              </Button>
            </div>
          </div>
        ),
        description: '',
        style: {
          padding: 0,
          backgroundColor: theme.colors.background,
        },
        className: 'notification-toast',
        duration: 10,
        placement: 'top',
        closeIcon: <div style={{ display: 'none' }} />,
        onClose() {
          notifications.forEach(notificationItem => {
            handleRemoveToastNotification(
              notificationItem.idAlarme,
              notificationItem.timeId,
            );
          });
        },
      });

      notifications.forEach(notificationItem => {
        markToastAsDisplayed(notificationItem.idAlarme);
      });
    },
    [
      api,
      t,
      navigate,
      theme.colors.background,
      handleRemoveToastNotification,
      markToastAsDisplayed,
    ],
  );

  useEffect(() => {
    if (openNotification && newToastNotifications.length > 0) {
      if (newToastNotifications.length === 1) {
        const singleNotification = newToastNotifications[0];
        const getTipoDisparo = (disparo: string) => {
          if (disparo === 'CRITICAL') return 'critical';
          if (disparo === 'OK') return 'ok';
          if (disparo === 'CRIT') return 'crit';
          return 'recovery';
        };

        openSingleNotificationHandler(
          {
            ...singleNotification,
            tipoDisparo: getTipoDisparo(singleNotification.disparo),
          },
          singleNotification,
        );
      } else {
        openMultipleNotificationsHandler(newToastNotifications);
      }
    }
  }, [
    openNotification,
    newToastNotifications,
    openSingleNotificationHandler,
    openMultipleNotificationsHandler,
  ]);

  return <>{contextHolder}</>;
}
